import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { UnassignedPicking } from "app/types/route";
import { FILTER_FIELD_SX } from "app/components/Layout/filterToolbarStyles";
import { getRelationName } from "app/utils/odooDisplay";
import { formatUtcScheduledDate } from "app/utils/timezone";
import {
  EMPTY_UNASSIGNED_ORDERS_FILTERS,
  UnassignedOrdersFilters,
  UnassignedOrdersSortDirection,
  UnassignedOrdersSortKey,
  filterUnassignedOrders,
  hasActiveUnassignedOrdersColumnFilters,
  hasActiveUnassignedOrdersFilters,
  sortUnassignedOrders,
} from "app/utils/unassignedOrdersTable";
import {
  UnassignedOrdersDateColumnFilter,
  UnassignedOrdersTextColumnFilter,
} from "./UnassignedOrdersColumnFilter";

const TABLE_HEAD_CELL_SX = {
  fontWeight: 700,
  fontSize: "0.78rem",
  letterSpacing: "0.01em",
  color: "#334155",
  bgcolor: "#f1f5f9",
  borderBottom: "1px solid #e2e8f0",
  boxShadow: "inset 0 -1px 0 rgba(15, 23, 42, 0.06)",
  whiteSpace: "nowrap" as const,
  py: 1.1,
  "& .MuiTableSortLabel-root": {
    fontWeight: 700,
    color: "#334155",
  },
  "& .MuiTableSortLabel-root:hover": {
    color: "#0f172a",
  },
  "& .MuiTableSortLabel-root.Mui-active": {
    color: "#0f172a",
  },
  "& .MuiTableSortLabel-icon": {
    color: "#64748b !important",
  },
};

const TABLE_FILTER_CELL_SX = {
  bgcolor: "#fafbfc",
  borderBottom: "1px solid #e2e8f0",
  py: 0.65,
  px: 1,
  verticalAlign: "middle" as const,
};

type UnassignedOrdersPanelProps = {
  orders: UnassignedPicking[];
  isLoading: boolean;
  selectedOrderIds: number[];
  onToggleOrderSelection: (orderId: number) => void;
  onAssignSelected?: () => void;
  userTimezone: string;
  embedded?: boolean;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
};

export function UnassignedOrdersPanel({
  orders,
  isLoading,
  selectedOrderIds,
  onToggleOrderSelection,
  onAssignSelected,
  userTimezone,
  embedded = false,
  expanded = false,
  onExpandedChange,
}: UnassignedOrdersPanelProps) {
  const [filters, setFilters] = useState<UnassignedOrdersFilters>(
    EMPTY_UNASSIGNED_ORDERS_FILTERS
  );
  const [sortKey, setSortKey] = useState<UnassignedOrdersSortKey>("scheduled_date");
  const [sortDirection, setSortDirection] =
    useState<UnassignedOrdersSortDirection>("desc");
  const [columnFiltersVisible, setColumnFiltersVisible] = useState(false);

  const filteredOrders = useMemo(
    () => filterUnassignedOrders(orders, filters, userTimezone),
    [filters, orders, userTimezone]
  );

  const visibleOrders = useMemo(
    () => sortUnassignedOrders(filteredOrders, sortKey, sortDirection),
    [filteredOrders, sortDirection, sortKey]
  );

  const filtersActive = hasActiveUnassignedOrdersFilters(filters);
  const columnFiltersActive = hasActiveUnassignedOrdersColumnFilters(filters);
  const showColumnFilterRow = columnFiltersVisible;

  const handleSort = (column: UnassignedOrdersSortKey) => {
    if (sortKey === column) {
      setSortDirection((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(column);
    setSortDirection(column === "scheduled_date" ? "desc" : "asc");
  };

  const clearFilters = () => {
    setFilters(EMPTY_UNASSIGNED_ORDERS_FILTERS);
  };

  const handleToggleExpanded = () => {
    onExpandedChange?.(!expanded);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        bgcolor: "#fff",
        border: "1px solid #e5e9ef",
        borderRadius: "14px",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        position: expanded ? "absolute" : "relative",
        bottom: 0,
        left: 0,
        right: 0,
        height: expanded
          ? { xs: "min(70vh, 520px)", lg: "min(72vh, 680px)" }
          : "100%",
        zIndex: expanded ? 30 : 1,
        boxShadow: expanded
          ? "0 -12px 40px rgba(15, 23, 42, 0.16), 0 -4px 14px rgba(15, 23, 42, 0.08)"
          : "none",
        transform: expanded ? "translateY(-2px)" : "none",
        transition:
          "height 0.28s ease, box-shadow 0.28s ease, transform 0.28s ease",
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1,
          borderBottom: "1px solid #eef1f4",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexWrap: { xs: "wrap", sm: "nowrap" },
          bgcolor: expanded ? "#fff" : "#fafbfc",
        }}
      >
        <Box sx={{ flexShrink: 0, minWidth: { lg: 168 } }}>
          <Tooltip title="Done outgoing pickings without a route">
            <Typography variant="subtitle2" fontWeight={700} noWrap>
              Unassigned Orders ({visibleOrders.length}
              {visibleOrders.length !== orders.length ? ` of ${orders.length}` : ""})
            </Typography>
          </Tooltip>
        </Box>

        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
            minWidth: 0,
            order: { xs: 3, sm: 0 },
            width: { xs: "100%", sm: "auto" },
          }}
        >
          <TextField
            size="small"
            placeholder="Search order, customer, origin..."
            value={filters.search}
            onChange={(event) =>
              setFilters((current) => ({ ...current, search: event.target.value }))
            }
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              flex: 1,
              width: "100%",
              minWidth: { xs: 0, sm: 220, md: 320 },
              maxWidth: "100%",
              ...FILTER_FIELD_SX,
            }}
          />

          <Tooltip title={columnFiltersVisible ? "Hide column filters" : "Show column filters"}>
            <IconButton
              size="small"
              aria-label="Toggle column filters"
              aria-pressed={columnFiltersVisible}
              onClick={() => setColumnFiltersVisible((visible) => !visible)}
              sx={{
                flexShrink: 0,
                width: 36,
                height: 36,
                border: "1px solid",
                borderColor:
                  columnFiltersVisible || columnFiltersActive ? "#94a3b8" : "#e2e8f0",
                borderRadius: "8px",
                bgcolor:
                  columnFiltersVisible || columnFiltersActive ? "#e2e8f0" : "#fff",
                color:
                  columnFiltersVisible || columnFiltersActive ? "#334155" : "#64748b",
                "&:hover": {
                  bgcolor: "#e2e8f0",
                  borderColor: "#94a3b8",
                },
              }}
            >
              <FilterListIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>

          {filtersActive && (
            <Button
              size="small"
              onClick={clearFilters}
              sx={{ textTransform: "none", color: "#64748b", flexShrink: 0, whiteSpace: "nowrap" }}
            >
              Clear
            </Button>
          )}

          {selectedOrderIds.length > 0 && onAssignSelected && (
            <Button
              size="small"
              variant="contained"
              onClick={onAssignSelected}
              sx={{
                flexShrink: 0,
                textTransform: "none",
                whiteSpace: "nowrap",
                bgcolor: "#7ec597",
                boxShadow: "none",
                fontWeight: 600,
                "&:hover": { bgcolor: "#6db486", boxShadow: "none" },
              }}
            >
              Assign Route{selectedOrderIds.length > 1 ? ` (${selectedOrderIds.length})` : ""}
            </Button>
          )}
        </Box>

        <Tooltip title={expanded ? "Collapse panel" : "Expand panel upward"}>
          <Button
            size="small"
            variant="outlined"
            disableElevation
            aria-label={expanded ? "Collapse unassigned orders panel" : "Expand unassigned orders panel"}
            aria-expanded={expanded}
            onClick={handleToggleExpanded}
            startIcon={
              expanded ? (
                <KeyboardArrowDownIcon fontSize="small" />
              ) : (
                <KeyboardArrowUpIcon fontSize="small" />
              )
            }
            sx={{
              flexShrink: 0,
              minWidth: 96,
              height: 36,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.8125rem",
              borderRadius: "8px",
              px: 1.5,
              color: "#334155",
              borderColor: expanded ? "#94a3b8" : "#cbd5e1",
              bgcolor: expanded ? "#f1f5f9" : "#fafbfc",
              boxShadow: "0 1px 3px rgba(15, 23, 42, 0.06)",
              "&:hover": {
                bgcolor: "#f1f5f9",
                borderColor: "#94a3b8",
                boxShadow: "0 2px 6px rgba(15, 23, 42, 0.1)",
              },
              "& .MuiButton-startIcon": {
                mr: 0.5,
                color: "#475569",
              },
            }}
          >
            {expanded ? "Collapse" : "Expand"}
          </Button>
        </Tooltip>
      </Box>

      <Box sx={{ flex: 1, overflow: "auto", minHeight: 0 }}>
        {isLoading ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <CircularProgress size={28} sx={{ color: "#7ec597" }} />
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: "100%" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" sx={TABLE_HEAD_CELL_SX} />
                  <TableCell sx={TABLE_HEAD_CELL_SX} sortDirection={sortKey === "name" ? sortDirection : false}>
                    <TableSortLabel
                      active={sortKey === "name"}
                      direction={sortKey === "name" ? sortDirection : "asc"}
                      onClick={() => handleSort("name")}
                    >
                      Order
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={TABLE_HEAD_CELL_SX} sortDirection={sortKey === "customer" ? sortDirection : false}>
                    <TableSortLabel
                      active={sortKey === "customer"}
                      direction={sortKey === "customer" ? sortDirection : "asc"}
                      onClick={() => handleSort("customer")}
                    >
                      Customer
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={TABLE_HEAD_CELL_SX} sortDirection={sortKey === "scheduled_date" ? sortDirection : false}>
                    <TableSortLabel
                      active={sortKey === "scheduled_date"}
                      direction={sortKey === "scheduled_date" ? sortDirection : "asc"}
                      onClick={() => handleSort("scheduled_date")}
                    >
                      Scheduled
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={TABLE_HEAD_CELL_SX} sortDirection={sortKey === "origin" ? sortDirection : false}>
                    <TableSortLabel
                      active={sortKey === "origin"}
                      direction={sortKey === "origin" ? sortDirection : "asc"}
                      onClick={() => handleSort("origin")}
                    >
                      Origin
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={TABLE_HEAD_CELL_SX}
                    sortDirection={sortKey === "loads" ? sortDirection : false}
                  >
                    <TableSortLabel
                      active={sortKey === "loads"}
                      direction={sortKey === "loads" ? sortDirection : "asc"}
                      onClick={() => handleSort("loads")}
                    >
                      Loads
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
                {showColumnFilterRow && (
                  <TableRow>
                    <TableCell padding="checkbox" sx={TABLE_FILTER_CELL_SX} />
                    <TableCell sx={TABLE_FILTER_CELL_SX}>
                      <UnassignedOrdersTextColumnFilter
                        label="order"
                        value={filters.name}
                        placeholder="Search order"
                        onApply={(value) =>
                          setFilters((current) => ({ ...current, name: value }))
                        }
                      />
                    </TableCell>
                    <TableCell sx={TABLE_FILTER_CELL_SX}>
                      <UnassignedOrdersTextColumnFilter
                        label="customer"
                        value={filters.customer}
                        placeholder="Search customer"
                        onApply={(value) =>
                          setFilters((current) => ({ ...current, customer: value }))
                        }
                      />
                    </TableCell>
                    <TableCell sx={TABLE_FILTER_CELL_SX}>
                      <UnassignedOrdersDateColumnFilter
                        scheduledFrom={filters.scheduledFrom}
                        scheduledTo={filters.scheduledTo}
                        onApply={(scheduledFrom, scheduledTo) =>
                          setFilters((current) => ({
                            ...current,
                            scheduledFrom,
                            scheduledTo,
                          }))
                        }
                      />
                    </TableCell>
                    <TableCell sx={TABLE_FILTER_CELL_SX}>
                      <UnassignedOrdersTextColumnFilter
                        label="origin"
                        value={filters.origin}
                        placeholder="Search origin"
                        onApply={(value) =>
                          setFilters((current) => ({ ...current, origin: value }))
                        }
                      />
                    </TableCell>
                    <TableCell align="right" sx={TABLE_FILTER_CELL_SX}>
                      <UnassignedOrdersTextColumnFilter
                        label="loads"
                        value={filters.loads}
                        placeholder="Search loads"
                        onApply={(value) =>
                          setFilters((current) => ({ ...current, loads: value }))
                        }
                      />
                    </TableCell>
                  </TableRow>
                )}
              </TableHead>
              <TableBody>
                {visibleOrders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2" color="textSecondary" sx={{ py: 1.5 }}>
                        {filtersActive
                          ? "No orders match your filters."
                          : "No unassigned orders"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  visibleOrders.map((order) => (
                    <TableRow
                      key={order.id}
                      hover
                      selected={selectedOrderIds.includes(order.id)}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedOrderIds.includes(order.id)}
                          onChange={() => onToggleOrderSelection(order.id)}
                          sx={{
                            color: "#9ca3af",
                            "&.Mui-checked": { color: "#5fa67a" },
                          }}
                        />
                      </TableCell>
                      <TableCell>{order.name}</TableCell>
                      <TableCell>{getRelationName(order.partner_id)}</TableCell>
                      <TableCell>
                        {formatUtcScheduledDate(
                          order.scheduled_date,
                          userTimezone,
                          "MM/DD/YYYY h:mm A"
                        )}
                      </TableCell>
                      <TableCell>{order.origin || "—"}</TableCell>
                      <TableCell align="right">
                        {order.shipment_packages_count ?? 0}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Paper>
  );
}
